import { countGraphemes } from "unicode-segmenter/grapheme";

export const supportCaseCategories = [
  "account-access",
  "reminders-bundles",
  "notifications-delivery",
  "privacy-rights",
  "accessibility",
  "security-unexpected-contact",
  "other-help-feedback",
] as const;
export type SupportCaseCategory = (typeof supportCaseCategories)[number];

export const supportCaseStates = [
  "received",
  "being-reviewed",
  "waiting-for-you",
  "resolved",
  "closed",
] as const;
export type SupportCaseState = (typeof supportCaseStates)[number];

export const supportCaseSeverities = ["S1", "S2", "S3", "S4"] as const;
export type SupportCaseSeverity = (typeof supportCaseSeverities)[number];
export type SupportCaseConcern = "problem" | "guidance" | "security-privacy";
export type SupportCaseImpact = Readonly<{
  serviceBlocked: boolean;
  workaroundAvailable: boolean;
  concern: SupportCaseConcern;
}>;

export type SupportCaseIntake = Readonly<{
  category: string;
  subject: string;
  body: string;
  impact: SupportCaseImpact;
}>;

function hasUnsupportedControl(value: string): boolean {
  return Array.from(value).some((character) => {
    const point = character.codePointAt(0) ?? -1;
    return (
      (point >= 0 && point <= 8) ||
      point === 11 ||
      point === 12 ||
      (point >= 14 && point <= 31) ||
      (point >= 127 && point <= 159)
    );
  });
}

function normalizeText(
  raw: string,
  field: "subject" | "message",
  graphemeMinimum: number,
  graphemeMaximum: number,
  byteMaximum: number,
): string {
  if (typeof raw !== "string") throw new RangeError(`Enter a ${field}.`);
  const normalized = raw.replaceAll("\r\n", "\n").replaceAll("\r", "\n").trim();
  if (hasUnsupportedControl(normalized) || (field === "subject" && /[\n\t]/u.test(normalized)))
    throw new RangeError("Remove unsupported control characters.");
  const graphemes = countGraphemes(normalized);
  if (graphemes < graphemeMinimum || graphemes > graphemeMaximum)
    throw new RangeError(
      field === "subject"
        ? "Use between 3 and 120 characters for the subject."
        : "Use between 1 and 2,000 characters for the message.",
    );
  if (new TextEncoder().encode(normalized).length > byteMaximum)
    throw new RangeError(`The ${field} is too large to send safely.`);
  return normalized;
}

export function normalizeSupportMessage(raw: string): string {
  return normalizeText(raw, "message", 1, 2_000, 8 * 1024);
}

export function normalizeSupportCaseIntake(value: SupportCaseIntake): Readonly<{
  category: SupportCaseCategory;
  subject: string;
  body: string;
  impact: SupportCaseImpact;
}> {
  if (!supportCaseCategories.includes(value.category as SupportCaseCategory))
    throw new RangeError("Choose an available support category.");
  if (
    typeof value.impact.serviceBlocked !== "boolean" ||
    typeof value.impact.workaroundAvailable !== "boolean" ||
    !["problem", "guidance", "security-privacy"].includes(value.impact.concern)
  )
    throw new RangeError("Choose the support impact answers again.");
  return {
    category: value.category as SupportCaseCategory,
    subject: normalizeText(value.subject, "subject", 3, 120, 512),
    body: normalizeSupportMessage(value.body),
    impact: { ...value.impact },
  };
}

/** Intake is only provisional: S1 always requires an authorized human decision. */
export function deriveProvisionalSupportSeverity(
  impact: SupportCaseImpact,
): Exclude<SupportCaseSeverity, "S1"> {
  if (impact.concern === "security-privacy") return "S2";
  if (impact.serviceBlocked && !impact.workaroundAvailable) return "S2";
  if (impact.serviceBlocked) return "S3";
  return "S4";
}

export function planSupportReply(
  state: SupportCaseState,
  sender: "requester" | "operator",
): Readonly<{ nextState: SupportCaseState; clearCloseDueAt: boolean }> {
  if (state === "closed") throw new RangeError("This support case is closed.");
  if (sender === "requester" && state === "resolved")
    return { nextState: "being-reviewed", clearCloseDueAt: true };
  if (sender === "requester" && state === "waiting-for-you")
    return { nextState: "being-reviewed", clearCloseDueAt: false };
  return { nextState: state, clearCloseDueAt: false };
}

export type SupportOperatorTransition = "triage" | "ask-requester" | "resolve";

/** Single lifecycle policy for human operator commands. */
export function planSupportOperatorTransition(
  state: SupportCaseState,
  transition: SupportOperatorTransition,
): SupportCaseState {
  if (state === "closed") throw new RangeError("This support case is closed.");
  if (transition === "triage") {
    if (state !== "received" && state !== "being-reviewed")
      throw new RangeError("Return the case to review before changing triage.");
    return state === "received" ? "being-reviewed" : state;
  }
  if (transition === "ask-requester") {
    if (state === "resolved")
      throw new RangeError("Review the resolution before asking a question.");
    return "waiting-for-you";
  }
  if (state !== "being-reviewed" && state !== "waiting-for-you")
    throw new RangeError("Review the case before resolving it.");
  return "resolved";
}

export function planRequesterResolutionConfirmation(state: SupportCaseState): "closed" {
  if (state !== "resolved") throw new RangeError("This support case is not ready to close.");
  return "closed";
}

export function advanceSupportReadWatermark(
  current: number,
  latestAuthorizedSequence: number,
  visibleThroughSequence: number,
): number {
  if (
    !Number.isSafeInteger(current) ||
    !Number.isSafeInteger(latestAuthorizedSequence) ||
    !Number.isSafeInteger(visibleThroughSequence) ||
    current < 0 ||
    latestAuthorizedSequence < 0 ||
    visibleThroughSequence < 0
  )
    throw new RangeError("The message position is unavailable.");
  return Math.max(current, Math.min(latestAuthorizedSequence, visibleThroughSequence));
}
